import React, { useCallback, useState, useEffect, useRef, Fragment } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { Sparkles, AlertCircle, ChevronDown, Check, ShieldUser } from 'lucide-react';
import clsx from 'clsx';
import { getTypeColorClass, getTypeBorderClass, isValidCodeName, normalizeCodeName } from '@utils/schemaUtils';
import { DataType, Priority, Parameter, EventSchema } from '@/types/schema';
import env from '@utils/env'; // Import env for API URL
import { AIGenerateContentType, AIGenerateContentResponse } from '@/types/ai'; // Import AI types
import { ParameterViewMode } from './ParameterListContainer'; // Import ParameterViewMode

interface ParameterTreeEditFormProps {
  param: Parameter;
  scopeType: 'global' | 'eventSpecific'; // Added scopeType prop
  selectedEvent: EventSchema | null;
  handleParameterChange: (paramId: string, updates: Partial<Parameter>) => void;
  isEditable: boolean;
  isEditing: boolean;
  activeEditingParamId: string | null;
  setActiveEditingParamId: React.Dispatch<React.SetStateAction<string | null>>;
  allEvents: EventSchema[];
  handleParameterAssociationChange: (propertyFullPath: string, eventId: string, isAssociated: boolean) => void;
  onExitEditing: (hasUnsavedChanges: boolean, hasErrors: boolean) => void; // Callback to parent
  viewMode: ParameterViewMode; // New prop for view mode
}

const ParameterTreeEditForm: React.FC<ParameterTreeEditFormProps> = ({
  param,
  selectedEvent,
  handleParameterChange,
  isEditable,
  isEditing,
  allEvents,
  handleParameterAssociationChange,
  onExitEditing,
  viewMode, // Destructure viewMode
}) => {
  // State for validation and UI
  const [codeNameError, setCodeNameError] = useState<string | null>(null);
  const [friendlyNameError, setFriendlyNameError] = useState<string | null>(null);
  const [isCodeNameManuallyEdited, setIsCodeNameManuallyEdited] = useState(false);
  const [isCodeNameAutoCompleteEnabled, setIsCodeNameAutoCompleteEnabled] = useState(false);
  
  // Local state for input values to avoid re-renders during typing
  const [localFriendlyName, setLocalFriendlyName] = useState(param.friendlyName);
  const [localCodeName, setLocalCodeName] = useState(param.codeName);
  const [localFullPath, setLocalFullPath] = useState(param.fullPath); // New state for fullPath
  const [localDescription, setLocalDescription] = useState(param.description);
  const [localRegexFormula, setLocalRegexFormula] = useState(param.regexFormula || '');
  const [localLlmInstructions, setLocalLlmInstructions] = useState(param.llmInstructions || '');
  const [localDataType, setLocalDataType] = useState(param.dataTypes[0] || 'string');
  const [localPriority, setLocalPriority] = useState(param.priorities[0] || 'Standard');
  const [localIsPii, setLocalIsPii] = useState(param.isPii || false);

  // Loading states for AI generation buttons
  const [isGeneratingRegex, setIsGeneratingRegex] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isGeneratingLlmInstructions, setIsGeneratingLlmInstructions] = useState(false);


  // Validation errors for fullPath
  const [fullPathError, setFullPathError] = useState<string | null>(null);

  // Refs
  const friendlyNameInputRef = useRef<HTMLInputElement>(null);
  const codeNameInputRef = useRef<HTMLInputElement>(null);
  const fullPathInputRef = useRef<HTMLInputElement>(null); // New ref for fullPath
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const regexInputRef = useRef<HTMLInputElement>(null);
  const llmInstructionsInputRef = useRef<HTMLTextAreaElement>(null);
  const editingFormRef = useRef<HTMLDivElement>(null); // Ref for the editing form container

  const isTypeDisabled = (param.dataTypes.includes('array') || param.dataTypes.includes('object')) && param.children && param.children.length > 0;

  // Validation functions
  const validateFriendlyName = useCallback((name: string) => {
    if (name.trim() === '') {
      setFriendlyNameError("Friendly Name can't be empty");
      return false;
    }
    setFriendlyNameError(null);
    return true;
  }, []);

  const validateCodeName = useCallback((name: string) => {
    if (name.trim() === '') {
      setCodeNameError("Code Name can't be empty");
      return false;
    }
    if (!isValidCodeName(name)) {
      setCodeNameError('Must contain only letters, numbers, underscores, hyphens, or spaces');
      return false;
    }
    setCodeNameError(null);
    return true;
  }, []);

  const validateFullPath = useCallback((path: string) => {
    if (path.trim() === '') {
      setFullPathError("Full Path can't be empty");
      return false;
    }
    if (path.startsWith('.') || path.endsWith('.')) {
      setFullPathError("Full Path cannot start or end with a dot");
      return false;
    }
    if (path.includes('..')) {
      setFullPathError("Full Path cannot contain '..'");
      return false;
    }
    // Validate each segment of the path using isValidCodeName
    const segments = path.split('.');
    for (const segment of segments) {
      if (!isValidCodeName(segment)) {
        setFullPathError(`Segment '${segment}' in Full Path is invalid. Must contain only letters, numbers, underscores, hyphens, or spaces.`);
        return false;
      }
    }
    setFullPathError(null);
    return true;
  }, [isValidCodeName]);

  // Sync local state with props when param changes or editing stops
  useEffect(() => {
    // Check if this is a new property (has default codeName 'new_property' or friendlyName is 'New Property')
    const isNewProperty = param.codeName === 'new_property' || param.friendlyName === 'New Property';
    
    if (isNewProperty) {
      // For new properties, start with empty fields
      setLocalFriendlyName('');
      setLocalCodeName('');
      // For tree view, use the param's fullPath (which should be set correctly by the parent)
      // For table view, start with empty full path
      setLocalFullPath(viewMode === 'tree' ? param.fullPath : '');
      setLocalDescription('');
      setLocalRegexFormula('');
      setLocalLlmInstructions('');
      setLocalDataType(param.dataTypes[0] || 'string');
      setLocalPriority(param.priorities[0] || 'Standard');
      setLocalIsPii(false); // Default to false for new properties
      setIsCodeNameManuallyEdited(false);
      setIsCodeNameAutoCompleteEnabled(true); // Enable auto-complete for new properties
    } else {
      // For existing properties, use their current values
      validateFriendlyName(param.friendlyName);
      validateCodeName(param.codeName); // Validate param.codeName directly
      validateFullPath(param.fullPath); // Validate param.fullPath directly
      setLocalFriendlyName(param.friendlyName);
      setLocalCodeName(param.codeName); // Use param.codeName directly
      setLocalFullPath(param.fullPath); // Use param.fullPath directly
      setLocalDescription(param.description);
      setLocalRegexFormula(param.regexFormula || '');
      setLocalLlmInstructions(param.llmInstructions || '');
      setLocalDataType(param.dataTypes[0] || 'string');
      setLocalPriority(param.priorities[0] || 'Standard');
      setLocalIsPii(param.isPii || false);
      setIsCodeNameManuallyEdited(false);
      setIsCodeNameAutoCompleteEnabled(false);
    }
    
    // Always clear errors when initializing
    setCodeNameError(null);
    setFriendlyNameError(null);
    setFullPathError(null); // Clear fullPath error
  }, [
    param.friendlyName,
    param.codeName,
    param.fullPath, // Added fullPath to dependencies
    param.description,
    param.regexFormula,
    param.llmInstructions,
    param.dataTypes,
    param.priorities,
    param.isPii,
    validateFriendlyName,
    validateCodeName,
    validateFullPath, // Added validateFullPath to dependencies
    viewMode // Added viewMode to dependencies
  ]);

  // Auto-focus on friendly name when editing starts
  useEffect(() => {
    if (friendlyNameInputRef.current) {
      friendlyNameInputRef.current.focus();
    }
  }, []);

  // Check if there are any actual changes compared to the original param
  const hasUnsavedChanges = useCallback(() => {
    return (
      localFriendlyName !== param.friendlyName ||
      localCodeName !== param.codeName || // Compare with param.codeName
      localFullPath !== param.fullPath || // Compare with param.fullPath
      localDescription !== param.description ||
      localRegexFormula !== (param.regexFormula || '') ||
      localLlmInstructions !== (param.llmInstructions || '') ||
      localDataType !== param.dataTypes[0] ||
      localPriority !== param.priorities[0] ||
      localIsPii !== (param.isPii || false)
    );
  }, [
    localFriendlyName, param.friendlyName,
    localCodeName, param.codeName,
    localFullPath, param.fullPath,
    localDescription, param.description,
    localRegexFormula, param.regexFormula,
    localLlmInstructions, param.llmInstructions,
    localDataType, param.dataTypes,
    localPriority, param.priorities,
    localIsPii, param.isPii
  ]);

  // Apply changes to parent state
  const applyChanges = useCallback(() => {
    // Re-validate before applying changes to catch any last-minute issues
    const isFriendlyNameValid = validateFriendlyName(localFriendlyName);
    const isCodeNameValid = validateCodeName(localCodeName);
    const isFullPathValid = validateFullPath(localFullPath); // Validate fullPath

    if (!isFriendlyNameValid || !isCodeNameValid || !isFullPathValid) { // Include fullPath validation
      return; // Prevent applying changes if validation fails
    }

    // Always send the complete current state of the parameter
    const updates: Partial<Parameter> = {
      friendlyName: localFriendlyName,
      codeName: localCodeName,
      fullPath: localFullPath, // Send fullPath
      description: localDescription,
      regexFormula: localRegexFormula,
      llmInstructions: localLlmInstructions,
      dataTypes: [localDataType as DataType],
      priorities: [localPriority as Priority],
      isPii: localIsPii, // Ensure PII is always sent
    };

    handleParameterChange(param.id, updates);
  }, [
    param.id,
    localFriendlyName,
    localCodeName,
    localFullPath, // Added localFullPath
    localDescription,
    localRegexFormula,
    localLlmInstructions,
    localDataType,
    localPriority,
    localIsPii,
    handleParameterChange,
    validateFriendlyName,
    validateCodeName,
    validateFullPath, // Added validateFullPath
    selectedEvent
  ]);

  const handleExitEditing = useCallback((e?: React.FocusEvent | React.KeyboardEvent) => {
    // Re-validate immediately before deciding to exit
    const isFriendlyNameValid = validateFriendlyName(localFriendlyName);
    const isCodeNameValid = validateCodeName(localCodeName);
    const isFullPathValid = validateFullPath(localFullPath); // Validate fullPath
    const hasErrors = !isFriendlyNameValid || !isCodeNameValid || !isFullPathValid; // Include fullPath errors
    const changesMade = hasUnsavedChanges();

    const isEscapePressed = e && e.type === 'keydown' && (e as React.KeyboardEvent).key === 'Escape';
    const isEnterPressed = e && e.type === 'keydown' && (e as React.KeyboardEvent).key === 'Enter';
    
    // Determine if focus is truly leaving the form
    const isFocusLeavingForm = !e || (e.type === 'blur' && editingFormRef.current &&
      !editingFormRef.current.contains((e as React.FocusEvent).relatedTarget as Node));

    if (isEscapePressed) {
      // On escape, always try to exit, but pass current error state
      onExitEditing(changesMade, hasErrors);
      return;
    }

    if (isEnterPressed && e && (e.currentTarget as HTMLElement).tagName !== 'TEXTAREA') {
      e.preventDefault(); // Prevent form submission
      if (!hasErrors) {
        applyChanges();
        onExitEditing(false, false); // Changes applied, no errors, can exit
      } else {
        // If there are errors on Enter, don't exit, and try to re-focus
        if (!isFriendlyNameValid && friendlyNameInputRef.current) {
          friendlyNameInputRef.current.focus();
        } else if (!isCodeNameValid && codeNameInputRef.current) {
          codeNameInputRef.current.focus();
        } else if (!isFullPathValid && fullPathInputRef.current) { // Re-focus fullPath
          fullPathInputRef.current.focus();
        }
      }
      return;
    }

    if (isFocusLeavingForm) {
      if (hasErrors) {
        // If focus is leaving and there are errors, prevent exit and re-focus
        if (!isFriendlyNameValid && friendlyNameInputRef.current) {
          friendlyNameInputRef.current.focus();
        } else if (!isCodeNameValid && codeNameInputRef.current) {
          codeNameInputRef.current.focus();
        } else if (!isFullPathValid && fullPathInputRef.current) { // Re-focus fullPath
          fullPathInputRef.current.focus();
        }
        return; // Prevent exit
      }

      // If no errors and focus is leaving, apply changes and exit
      if (changesMade) {
        applyChanges(); // Apply changes before exiting
        onExitEditing(false, false); // Changes applied, no errors, can exit
      } else {
        onExitEditing(false, false); // No changes, no errors, can exit
      }
      return;
    }
  }, [
    localFriendlyName,
    localCodeName,
    localFullPath, // Added localFullPath
    hasUnsavedChanges,
    applyChanges,
    validateFriendlyName,
    validateCodeName,
    validateFullPath, // Added validateFullPath
    onExitEditing
  ]);

  // Generic blur handler for all inputs
  const handleInputBlur = useCallback((e: React.FocusEvent) => {
    if (e.target === friendlyNameInputRef.current) {
      validateFriendlyName(localFriendlyName);
    } else if (e.target === codeNameInputRef.current) {
      validateCodeName(localCodeName);
    } else if (e.target === fullPathInputRef.current) { // Handle fullPath blur
      validateFullPath(localFullPath);
    }
    handleExitEditing(e);
  }, [handleExitEditing, localFriendlyName, localCodeName, localFullPath, validateFriendlyName, validateCodeName, validateFullPath]);

  // Generic keydown handler for all inputs
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      handleExitEditing(e);
    }
  }, [handleExitEditing]);

  // Effect to handle clicks outside the editing form
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingFormRef.current && !editingFormRef.current.contains(event.target as Node)) {
        handleExitEditing();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleExitEditing]);

  const handleFriendlyNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newFriendlyName = e.target.value;
    setLocalFriendlyName(newFriendlyName);
    validateFriendlyName(newFriendlyName);

    if (isCodeNameAutoCompleteEnabled && !isCodeNameManuallyEdited) {
      const newCodeName = normalizeCodeName(newFriendlyName);
      setLocalCodeName(newCodeName);
      validateCodeName(newCodeName);
      // Also update fullPath if it's a new property and not manually edited
      if (param.isNew && !isCodeNameManuallyEdited) { // Assuming param.isNew indicates a new property
        setLocalFullPath(newCodeName);
        validateFullPath(newCodeName);
      }
    }
  }, [isCodeNameAutoCompleteEnabled, isCodeNameManuallyEdited, validateFriendlyName, validateCodeName, param.isNew, validateFullPath]);

  const handleCodeNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalCodeName(newValue);
    setIsCodeNameManuallyEdited(true);
    setIsCodeNameAutoCompleteEnabled(false); // Disable auto-complete if user manually edits
    validateCodeName(newValue);
    // If codeName is changed, and it's a root property, update fullPath
    if (!param.parentPaths || param.parentPaths.length === 0) {
      setLocalFullPath(newValue);
      validateFullPath(newValue);
    }
  }, [validateCodeName, param.parentPaths, validateFullPath]);

  const handleFullPathChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newFullPath = e.target.value;
    setLocalFullPath(newFullPath);
    validateFullPath(newFullPath);

    // Automatically update codeName from the last segment of the fullPath
    const segments = newFullPath.split('.');
    const newCodeName = segments[segments.length - 1] || '';
    setLocalCodeName(newCodeName);
    setIsCodeNameManuallyEdited(true); // Mark as manually edited since fullPath was changed
    setIsCodeNameAutoCompleteEnabled(false); // Disable auto-complete
    validateCodeName(newCodeName);
  }, [validateFullPath, validateCodeName]);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalDescription(e.target.value);
  }, []);

  const handleRegexChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalRegexFormula(e.target.value);
  }, []);

  const handleLlmInstructionsChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalLlmInstructions(e.target.value);
  }, []);

  const handleDataTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalDataType(e.target.value as DataType);
  }, []);

  const handlePriorityChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalPriority(e.target.value as Priority);
  }, []);

  const handleIsPiiChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalIsPii(e.target.checked);
  }, []);

  // Helper function to recursively apply event association changes to children
  const applyEventAssociationToChildren = useCallback((
    currentParam: Parameter,
    eventId: string,
    isAssociated: boolean
  ) => {
    if (currentParam.children && currentParam.children.length > 0) {
      currentParam.children.forEach(child => {
        handleParameterAssociationChange(child.fullPath, eventId, isAssociated);
        // Recursively apply to grand-children
        applyEventAssociationToChildren(child, eventId, isAssociated);
      });
    }
  }, [handleParameterAssociationChange]);

  const callAIGeneration = useCallback(async (contentType: AIGenerateContentType, currentValue: string) => {
    const backendUrl = env.apiUrl;
    const apiUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
    
    try {
      const response = await fetch(`${apiUrl}/api/ai/generateContent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contentType,
          context: {
            friendlyName: localFriendlyName,
            description: localDescription,
            dataType: localDataType,
            currentValue: currentValue,
            fullPath: localFullPath, // Use localFullPath
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`AI generation failed for ${contentType}:`, errorData);
        throw new Error(errorData.error || `Failed to generate ${contentType}`);
      }

      const data: AIGenerateContentResponse = await response.json();
      return data.generatedContent;

    } catch (error) {
      console.error(`Error calling AI generation for ${contentType}:`, error);
      // Optionally show a user-facing error message
      return null;
    }
  }, [localFriendlyName, localDescription, localDataType, localFullPath]); // Added localFullPath

    const handleGenerateRegex = useCallback(async () => {
      setIsGeneratingRegex(true);
      const generated = await callAIGeneration('regex', localRegexFormula);
      if (generated !== null && generated !== undefined) { // Check for undefined as well
        setLocalRegexFormula(generated);
      }
      setIsGeneratingRegex(false);
    }, [callAIGeneration, localRegexFormula]);
  
    const handleGenerateDescription = useCallback(async () => {
      setIsGeneratingDescription(true);
      const generated = await callAIGeneration('description', localDescription);
      if (generated !== null && generated !== undefined) { // Check for undefined as well
        setLocalDescription(generated);
      }
      setIsGeneratingDescription(false);
    }, [callAIGeneration, localDescription]);
  
    const handleGenerateLlmInstructions = useCallback(async () => {
      setIsGeneratingLlmInstructions(true);
      const generated = await callAIGeneration('llmInstructions', localLlmInstructions);
      if (generated !== null && generated !== undefined) { // Check for undefined as well
        setLocalLlmInstructions(generated);
      }
      setIsGeneratingLlmInstructions(false);
    }, [callAIGeneration, localLlmInstructions]);

  const handleEventSelectChange = useCallback((selectedEvents: EventSchema[]) => {
    const newSelectedEventIds = selectedEvents.map(event => event.id);
    const oldSelectedEventIds = allEvents.filter(event =>
      event.parameterFullPaths.includes(param.fullPath)
    ).map(event => event.id);

    const addedEventIds = newSelectedEventIds.filter((id: string) => !oldSelectedEventIds.includes(id));
    const removedEventIds = oldSelectedEventIds.filter((id: string) => !newSelectedEventIds.includes(id));

    const isObjectOrArray = param.dataTypes.includes('array') || param.dataTypes.includes('object');
    const hasChildren = param.children && param.children.length > 0;
    const hasChanges = addedEventIds.length > 0 || removedEventIds.length > 0;

    if (hasChanges) {
      // Apply changes to the parent parameter
      addedEventIds.forEach((eventId: string) => {
        handleParameterAssociationChange(param.fullPath, eventId, true);
      });
      removedEventIds.forEach((eventId: string) => {
        handleParameterAssociationChange(param.fullPath, eventId, false);
      });

      // If it's an object/array with children, also apply changes to all children automatically
      if (isObjectOrArray && hasChildren) {
        // Apply changes to children
        addedEventIds.forEach((eventId: string) => {
          applyEventAssociationToChildren(param, eventId, true);
        });
        removedEventIds.forEach((eventId: string) => {
          applyEventAssociationToChildren(param, eventId, false);
        });
      }
    }
  }, [param, allEvents, handleParameterAssociationChange, applyEventAssociationToChildren]);

  const currentEventAssociations = allEvents.filter(event =>
    event.parameterFullPaths.includes(param.fullPath)
  );

  return (
    <>
      <div 
        ref={editingFormRef} 
        className={clsx(
          "flex flex-col gap-3 shadow w-full bg-blue-50 dark:bg-gray-900",
          viewMode !== 'table' && 'rounded-lg',
          isEditing && viewMode !== 'table' && "border border-blue-200 dark:border-gray-950",
          "py-4 px-4"
        )}
      >
        {/* Friendly name and data type */}
        <div className="flex flex-row gap-3 w-full">
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Friendly Name
          </label>
            <input
              ref={friendlyNameInputRef}
              type="text"
              value={localFriendlyName}
              onChange={handleFriendlyNameChange}
              onFocus={useCallback(() => {
                // Always enable auto-complete if code name is empty
                if (localCodeName.trim() === '') {
                  setIsCodeNameAutoCompleteEnabled(true);
                  setIsCodeNameManuallyEdited(false); // Reset manual edit flag if code name is empty
                }
              }, [localCodeName])}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              className={clsx(
                "w-full px-3 py-2 border rounded focus:outline-none transition duration-300 ease-in-out text-sm",
                friendlyNameError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20',
                "dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              )}
              placeholder="Friendly Name"
              disabled={!isEditable}
              tabIndex={1}
            />
            {friendlyNameError && (
              <p className="mt-1 text-xs text-red-500 flex items-center">
                <AlertCircle size={12} className="inline mr-1" />
                {friendlyNameError}
              </p>
            )}
          </div>
          
          <div className="w-40">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data Type
            </label>
            <div className="relative">
              <select
                value={localDataType}
                onChange={handleDataTypeChange}
                onBlur={handleInputBlur}
                disabled={isTypeDisabled || !isEditable}
                className={clsx(
                  `w-full h-10 px-3 pr-4 py-2 border rounded focus:outline-none transition duration-300 ease-in-out text-sm appearance-none`,
                  `${getTypeBorderClass(localDataType, isTypeDisabled || !isEditable)} ${getTypeColorClass(localDataType)}`,
                  `dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20`,
                  `dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500`,
                  isTypeDisabled || !isEditable ? 'opacity-60 cursor-not-allowed' : ''
                )}
                tabIndex={2}
              >
                {['string', 'number', 'boolean', 'array', 'object', 'null'].map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-300 pointer-events-none" />
            </div>
          </div>
        </div>

        {viewMode === 'table' ? (
          <div className="flex flex-row gap-3 w-full">
            {/* Full Path Input for Table View */}
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Full Path
              </label>
              <input
                ref={fullPathInputRef}
                type="text"
                value={localFullPath}
                onChange={handleFullPathChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                className={clsx(
                  "w-full px-3 py-2 border rounded focus:outline-none transition duration-300 ease-in-out text-sm",
                  fullPathError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20',
                  "dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                )}
                placeholder="full.path.to.property"
                disabled={!isEditable}
                tabIndex={3}
              />
              {fullPathError && (
                <p className="mt-1 text-xs text-red-500 flex items-center">
                  <AlertCircle size={12} className="inline mr-1" />
                  {fullPathError}
                </p>
              )}
            </div>
            {/* Regex Formula */}
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Regex Formula
              </label>
              <div className="relative">
                <input
                  ref={regexInputRef}
                  type="text"
                  value={localRegexFormula}
                  onChange={handleRegexChange}
                  onBlur={handleInputBlur}
                  onKeyDown={handleInputKeyDown}
                  className="w-full px-3 py-2 pr-10 border rounded focus:outline-none transition duration-300 ease-in-out text-sm border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="Regex Pattern"
                  disabled={!isEditable || isGeneratingRegex}
                  tabIndex={4}
                />
                <button
                  type="button"
                  onClick={handleGenerateRegex}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 ${!isEditable || isGeneratingRegex ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Generate Regex with AI"
                  disabled={!isEditable || isGeneratingRegex}
                >
                  {isGeneratingRegex ? (
                    <div className="rounded-full h-4 w-4 border-b-2 border-blue-500 animate-spin"></div>
                  ) : (
                    <Sparkles size={16} />
                  )}
                </button>
              </div>
            </div>
            <div className="w-40">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <div className="relative">
                <select
                  value={localPriority}
                  onChange={handlePriorityChange}
                  onBlur={handleInputBlur}
                  className={clsx(
                    `w-full h-10 px-3 pr-4 py-2 border rounded focus:outline-none transition duration-300 ease-in-out text-sm appearance-none`,
                    `border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20`,
                    `dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500`,
                    param.priorities[0] === 'Required' ? 'text-red-600 dark:text-red-400 font-medium' : ''
                  )}
                  disabled={!isEditable}
                  tabIndex={5}
                >
                  {['Required', 'Standard', 'Optional'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-300 pointer-events-none" />
              </div>
            </div>
          </div>
        ) : (
          /* Code name with prefix for Tree View */
          <div className="flex flex-row gap-3 w-full">
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Code Name (Last Segment of Path)
              </label>
              <div className="flex items-center w-full border rounded focus-within:ring focus-within:border-blue-500 focus-within:ring-blue-500/30 dark:focus-within:ring-blue-500/20 transition duration-300 ease-in-out text-sm
                border-gray-300 dark:border-gray-700 dark:focus-within:border-blue-600
                bg-white dark:bg-gray-800 dark:focus-within:bg-gray-700 text-gray-900 dark:text-gray-100">
                {param.parentPaths && param.parentPaths.length > 0 && (
                  <span className="px-3 py-2 text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-l-sm border-r border-gray-300 dark:border-gray-700 flex-shrink-0">
                    {param.parentPaths.join('.')}.
                  </span>
                )}
                <input
                  ref={codeNameInputRef}
                  type="text"
                  value={localCodeName}
                  onChange={handleCodeNameChange}
                  onBlur={handleInputBlur}
                  onKeyDown={handleInputKeyDown}
                  className={clsx(
                    `flex-1 min-w-0 px-3 py-2 focus:outline-none text-sm bg-transparent`,
                    codeNameError ? 'text-red-500' : '',
                    !isEditable ? 'opacity-60 cursor-not-allowed' : ''
                  )}
                  placeholder="code_name"
                  disabled={!isEditable}
                  tabIndex={3}
                />
              </div>
              {codeNameError && (
                <p className="mt-1 text-xs text-red-500 flex items-center">
                  <AlertCircle size={12} className="inline mr-1" />
                  {codeNameError}
                </p>
              )}
            </div>
            {/* Regex Formula */}
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Regex Formula
              </label>
              <div className="relative">
                <input
                  ref={regexInputRef}
                  type="text"
                  value={localRegexFormula}
                  onChange={handleRegexChange}
                  onBlur={handleInputBlur}
                  onKeyDown={handleInputKeyDown}
                  className="w-full px-3 py-2 pr-10 border rounded focus:outline-none transition duration-300 ease-in-out text-sm border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="Regex Pattern"
                  disabled={!isEditable || isGeneratingRegex}
                  tabIndex={4}
                />
                <button
                  type="button"
                  onClick={handleGenerateRegex}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 ${!isEditable || isGeneratingRegex ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Generate Regex with AI"
                  disabled={!isEditable || isGeneratingRegex}
                >
                  {isGeneratingRegex ? (
                    <div className="rounded-full h-4 w-4 border-b-2 border-blue-500 animate-spin"></div>
                  ) : (
                    <Sparkles size={16} />
                  )}
                </button>
              </div>
            </div>
            {/* Priority */}
            <div className="w-40">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <div className="relative">
                <select
                  value={localPriority}
                  onChange={handlePriorityChange}
                  onBlur={handleInputBlur}
                  className={clsx(
                    `w-full h-10 px-3 pr-4 py-2 border rounded focus:outline-none transition duration-300 ease-in-out text-sm appearance-none`,
                    `border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20`,
                    `dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500`,
                    param.priorities[0] === 'Required' ? 'text-red-600 dark:text-red-400 font-medium' : ''
                  )}
                  disabled={!isEditable}
                  tabIndex={5}
                >
                  {['Required', 'Standard', 'Optional'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-300 pointer-events-none" />
              </div>
            </div>
          </div>
        )}
        
        {/* Description and PII Checkbox */}
        <div className="flex flex-row gap-3 w-full items-end">
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <div className="relative">
              <input
                ref={descriptionInputRef}
                type="text"
                value={localDescription}
                onChange={handleDescriptionChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                className="w-full px-3 py-2 pr-10 border rounded focus:outline-none transition duration-300 ease-in-out text-sm border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                placeholder="Description"
                disabled={!isEditable || isGeneratingDescription}
                tabIndex={6}
              />
              <button
                type="button"
                onClick={handleGenerateDescription}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 ${!isEditable || isGeneratingDescription ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Generate Description with AI"
                disabled={!isEditable || isGeneratingDescription}
              >
                {isGeneratingDescription ? (
                  <div className="rounded-full h-4 w-4 border-b-2 border-blue-500 animate-spin"></div>
                ) : (
                  <Sparkles size={16} />
                )}
              </button>
            </div>
          </div>
          
          <div className="w-40 flex items-center justify-start h-10"> {/* Adjusted for checkbox alignment */}
            <input
              type="checkbox"
              id={`isPii-${param.id}`}
              checked={localIsPii}
              onChange={handleIsPiiChange}
              disabled={!isEditable}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:checked:bg-blue-600 dark:checked:border-blue-600"
              tabIndex={7}
            />
            <label htmlFor={`isPii-${param.id}`} className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
              <ShieldUser size={16} className="text-fuchsia-500" /> Mark as PII
            </label>
          </div>
        </div>
        
        {/* LLM Instructions */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            LLM Instructions
          </label>
          <div className="relative">
            <textarea
              ref={llmInstructionsInputRef}
              value={localLlmInstructions}
              onChange={handleLlmInstructionsChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              className="w-full px-3 py-2 pr-10 border rounded focus:outline-none transition duration-300 ease-in-out text-sm border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              placeholder="Instructions for LLM"
              disabled={!isEditable || isGeneratingLlmInstructions}
              rows={2}
              tabIndex={8}
            />
            <button
              type="button"
              onClick={handleGenerateLlmInstructions}
              className={`absolute right-2 top-3 p-1 rounded-md text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 ${!isEditable || isGeneratingLlmInstructions ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Generate LLM Instructions with AI"
              disabled={!isEditable || isGeneratingLlmInstructions}
            >
              {isGeneratingLlmInstructions ? (
                <div className="rounded-full h-4 w-4 border-b-2 border-blue-500 animate-spin"></div>
              ) : (
                <Sparkles size={16} />
              )}
            </button>
          </div>
        </div>

        {/* Event Association Multi-select */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Associated Events
          </label>
          <div className="flex flex-row gap-2 items-end w-full"> {/* Added flex container for Listbox and button */}
            <Listbox
              value={currentEventAssociations}
              onChange={handleEventSelectChange as (value: EventSchema[]) => void}
              multiple
              disabled={!isEditable}
            >
              {({ open }) => (
                <div className="relative flex-1"> {/* flex-1 to make Listbox take available space */}
                  <ListboxButton
                    className={clsx(
                      "relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm",
                      "border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500/30 dark:focus:ring-blue-500/20",
                      "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100",
                      !isEditable ? 'opacity-60 cursor-not-allowed' : ''
                    )}
                    tabIndex={9}
                  >
                    <div className="flex flex-wrap gap-1 pr-8">
                      {currentEventAssociations.length > 0 ? (
                        currentEventAssociations.map(event => (
                          <span
                            key={event.id}
                            className="inline-flex items-center rounded-full bg-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-900 dark:bg-blue-700 dark:text-blue-100"
                          >
                            {event.name}
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent Listbox from opening
                                handleEventSelectChange(currentEventAssociations.filter(evt => evt.id !== event.id));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.stopPropagation();
                                  handleEventSelectChange(currentEventAssociations.filter(evt => evt.id !== event.id));
                                }
                              }}
                              className="ml-1 -mr-0.5 h-4 w-4 flex-shrink-0 rounded-full hover:bg-blue-300 dark:hover:bg-blue-600 inline-flex items-center justify-center text-blue-900 dark:text-blue-100"
                            >
                              <span className="sr-only">Remove {event.name}</span>
                              <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                              </svg>
                            </span>
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">Select events...</span>
                      )}
                    </div>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDown
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </ListboxButton>

                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions
                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm
                        dark:bg-gray-800 dark:border dark:border-gray-700"
                    >
                      {allEvents.map((event) => (
                        <ListboxOption
                          key={event.id}
                          className={({ active }) =>
                            clsx(
                              active ? 'text-white bg-blue-600' : 'text-gray-900 dark:text-gray-100',
                              'relative cursor-default select-none py-2 pl-10 pr-4'
                            )
                          }
                          value={event}
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                {event.name}
                              </span>
                              {selected ? (
                                <span
                                  className={clsx(
                                    active ? 'text-white' : 'text-blue-600',
                                    'absolute inset-y-0 left-0 flex items-center pl-3'
                                  )}
                                >
                                  <Check className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </div>
              )}
            </Listbox>
            <button
              type="button"
              onClick={() => {
                handleEventSelectChange(allEvents);
              }}
              disabled={!isEditable || currentEventAssociations.length === allEvents.length}
              className={clsx(
                "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
                "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600",
                !isEditable || currentEventAssociations.length === allEvents.length ? 'opacity-60 cursor-not-allowed' : ''
              )}
            >
              Assign All
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParameterTreeEditForm;
